'use client';

import { useEffect, useState } from 'react';
import { apiUrl } from '@lib/constants';
import type { ServiceKey } from '@caverne/shared';

export interface Me {
  userId: number;
  email: string;
  displayName: string;
  services: ServiceKey[];
  isManager: boolean;
  /** Sites atteignables : ceux de rattachement, plus ceux que l'utilisateur encadre. */
  sites: Array<{ id: number; code: string; label: string }>;
  /** Sous-ensemble de `sites` dont l'utilisateur est référent — il y agit sur tout le matériel. */
  refereeSiteIds: number[];
  manager: { email: string; name: string | null } | null;
}

type AuthState =
  | { status: 'loading'; me: null }
  | { status: 'authenticated'; me: Me }
  | { status: 'unauthenticated'; me: null };

/**
 * Charge le profil courant via GET /api/me (protégé par authGuard).
 * 200 → authentifié (profil complet) ; 401 → non authentifié.
 */
export function useAuth(): AuthState {
  const [state, setState] = useState<AuthState>({ status: 'loading', me: null });

  useEffect(() => {
    let cancelled = false;

    fetch(apiUrl('/api/me'), { credentials: 'include' })
      .then(async (res) => {
        if (cancelled) return;
        if (res.ok) {
          setState({ status: 'authenticated', me: (await res.json()) as Me });
        } else {
          setState({ status: 'unauthenticated', me: null });
        }
      })
      .catch(() => {
        if (!cancelled) setState({ status: 'unauthenticated', me: null });
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}
